/**
 * Profile Page
 *
 * User profile with account settings and password change
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  User,
  Mail,
  Building2,
  Shield,
  Clock,
  Calendar,
  Key,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Input } from '@/presentation/components/ui/Input'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { useMyProfile, useChangeMyPassword } from '@/application/users/useUsers'
import { useTwoFactorStatus } from '@/application/auth/useTwoFactor'
import { Setup2FAModal } from '@/presentation/components/auth/Setup2FAModal'
import { Disable2FAModal } from '@/presentation/components/auth/Disable2FAModal'
import { USER_ROLES } from '@/config/constants'

// =============================================================================
// Schemas
// =============================================================================

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'La contraseña actual es requerida'),
    newPassword: z.string().min(8, 'La nueva contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string().min(1, 'Confirma la nueva contraseña'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })

type ChangePasswordForm = z.infer<typeof changePasswordSchema>

// =============================================================================
// Helpers
// =============================================================================

const ROLE_LABELS: Record<string, string> = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Administrador',
  [USER_ROLES.ADMIN]: 'Administrador',
  [USER_ROLES.RADIOLOGIST]: 'Radiólogo',
  [USER_ROLES.DOCTOR]: 'Médico',
  [USER_ROLES.TECHNICIAN]: 'Técnico',
  [USER_ROLES.TRANSCRIPTOR]: 'Transcriptor',
  [USER_ROLES.PATIENT]: 'Paciente',
}

const ROLE_VARIANTS: Record<string, 'default' | 'destructive' | 'warning' | 'success' | 'secondary'> = {
  [USER_ROLES.SUPER_ADMIN]: 'destructive',
  [USER_ROLES.ADMIN]: 'warning',
  [USER_ROLES.RADIOLOGIST]: 'default',
  [USER_ROLES.DOCTOR]: 'success',
  [USER_ROLES.TECHNICIAN]: 'secondary',
  [USER_ROLES.TRANSCRIPTOR]: 'secondary',
  [USER_ROLES.PATIENT]: 'secondary',
}

// =============================================================================
// Component
// =============================================================================

export function ProfilePage() {
  const { data: profile, isLoading } = useMyProfile()
  const { data: twoFactorStatus, refetch: refetch2FAStatus } = useTwoFactorStatus()
  const changePasswordMutation = useChangeMyPassword()
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [showSetup2FA, setShowSetup2FA] = useState(false)
  const [showDisable2FA, setShowDisable2FA] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onChangePassword = async (data: ChangePasswordForm) => {
    setPasswordSuccess(null)
    setPasswordError(null)

    changePasswordMutation.mutate(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          setPasswordSuccess('Contraseña actualizada correctamente')
          reset()
        },
        onError: (error: any) => {
          const message = error.response?.data?.message || error.message || 'Error al cambiar contraseña'
          setPasswordError(message)
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-[var(--primary)]" />
              Información de la Cuenta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[var(--primary)]/10">
                <Mail className="h-4 w-4 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Email</p>
                <p className="font-medium text-[var(--foreground)]">{profile?.email}</p>
              </div>
            </div>

            {/* Role */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[var(--primary)]/10">
                <Shield className="h-4 w-4 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Rol</p>
                <Badge variant={ROLE_VARIANTS[profile?.role || ''] || 'default'}>
                  {ROLE_LABELS[profile?.role || ''] || profile?.role}
                </Badge>
              </div>
            </div>

            {/* Institution */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[var(--primary)]/10">
                <Building2 className="h-4 w-4 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Institución</p>
                <p className="font-medium text-[var(--foreground)]">
                  {profile?.institutionName || (
                    <span className="text-amber-600 dark:text-amber-400">Sin institución</span>
                  )}
                </p>
              </div>
            </div>

            {/* Last Login */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[var(--primary)]/10">
                <Clock className="h-4 w-4 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Último acceso</p>
                <p className="font-medium text-[var(--foreground)]">
                  {profile?.lastLoginAt
                    ? format(new Date(profile.lastLoginAt), "dd 'de' MMMM yyyy, HH:mm", {
                        locale: es,
                      })
                    : 'Sin registro'}
                </p>
              </div>
            </div>

            {/* Account Created */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[var(--primary)]/10">
                <Calendar className="h-4 w-4 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Cuenta creada</p>
                <p className="font-medium text-[var(--foreground)]">
                  {profile?.createdAt
                    ? format(new Date(profile.createdAt), "dd 'de' MMMM yyyy", {
                        locale: es,
                      })
                    : 'Sin registro'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-[var(--primary)]" />
              Cambiar Contraseña
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onChangePassword)} className="space-y-4">
              {/* Success Message */}
              {passwordSuccess && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm">{passwordSuccess}</span>
                </div>
              )}

              {/* Error Message */}
              {passwordError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)]">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{passwordError}</span>
                </div>
              )}

              <Input
                label="Contraseña actual"
                type="password"
                placeholder="********"
                error={errors.currentPassword?.message}
                {...register('currentPassword')}
              />

              <Input
                label="Nueva contraseña"
                type="password"
                placeholder="********"
                error={errors.newPassword?.message}
                {...register('newPassword')}
              />

              <Input
                label="Confirmar nueva contraseña"
                type="password"
                placeholder="********"
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Cambiando contraseña...
                  </>
                ) : (
                  'Cambiar Contraseña'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* 2FA Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-[var(--primary)]" />
            Autenticación de Dos Factores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${twoFactorStatus?.isEnabled ? 'bg-green-500/10' : 'bg-amber-500/10'}`}>
                <Shield className={`h-5 w-5 ${twoFactorStatus?.isEnabled ? 'text-green-500' : 'text-amber-500'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-[var(--foreground)]">
                    {twoFactorStatus?.isEnabled ? '2FA Activado' : '2FA Desactivado'}
                  </p>
                  {twoFactorStatus?.isEnabled && (
                    <Badge variant="success">Protegido</Badge>
                  )}
                </div>
                <p className="text-sm text-[var(--foreground-muted)]">
                  {twoFactorStatus?.isEnabled
                    ? `Tu cuenta está protegida. ${twoFactorStatus.backupCodesRemaining} códigos de respaldo disponibles.`
                    : 'Agrega una capa extra de seguridad a tu cuenta usando una aplicación de autenticación.'}
                </p>
              </div>
            </div>
            {twoFactorStatus?.isEnabled ? (
              <Button variant="outline" size="sm" onClick={() => setShowDisable2FA(true)}>
                Desactivar
              </Button>
            ) : (
              <Button size="sm" onClick={() => setShowSetup2FA(true)}>
                Configurar 2FA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2FA Modals */}
      <Setup2FAModal
        open={showSetup2FA}
        onOpenChange={setShowSetup2FA}
        onSuccess={() => refetch2FAStatus()}
      />
      <Disable2FAModal
        open={showDisable2FA}
        onOpenChange={setShowDisable2FA}
        onSuccess={() => refetch2FAStatus()}
      />
    </div>
  )
}
